// pages/user/future-Gifts.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TimeIcon, ArrowRight } from '../../../public/icons';
import orderService from '../../../services/orderService';
import MagnetsFrameModal from '../../../component/order/MagnetsFrameModal';
import AudioGiftModal from '../../../component/order/AudioGiftModal';
import FutureGiftModal from '../../../component/order/FutureGiftModal';

const FutureGifts = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [gifts, setGifts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  /* 🟢 Inline modals for content creation */
  const [magnetsModal, setMagnetsModal] = useState({
    open: false,
    orderId: null,
    giftId: null,
    gift: null,
    orderNumber: null
  });
  const [audioModal, setAudioModal] = useState({
    open: false,
    orderId: null,
    giftId: null,
    gift: null,
    orderNumber: null
  });

  /* 🟢 Detail modal */
  const [detailModal, setDetailModal] = useState({
    open: false,
    order: null,
    gift: null
  });

  /* ── Fetch every future gift across the user's orders ── */
  const fetchGifts = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await orderService.getMyOrders({ limit: 100 });
      const orders = Array.isArray(res?.data) ? res.data : [];

      /* Flatten all future gifts, keeping a reference to their parent order */
      const flat = [];
      orders.forEach((order) => {
        const futureGifts =
          order?.items?.[0]?.customization?.futureGift?.futureGifts || [];
        futureGifts.forEach((gift, index) => {
          flat.push({
            ...gift,
            orderId: order.id || order._id,
            order,                                 // 🟢 full order object
            orderNumber: order.orderNumber,
            orderDate: order.date,
            orderTime: order.time,
            shippingAddress:
              order.shippingAddress ||
              [
                order.delivery?.address,
                order.delivery?.city,
                order.delivery?.state,
                order.delivery?.country
              ]
                .filter(Boolean)
                .join(', '),
            giftIndex: index
          });
        });
      });

      /* Sort by nearest delivery date */
      flat.sort((a, b) => {
        const da = a.deliveryDate
          ? new Date(a.deliveryDate).getTime()
          : Infinity;
        const db = b.deliveryDate
          ? new Date(b.deliveryDate).getTime()
          : Infinity;
        return da - db;
      });

      setGifts(flat);
    } catch (err) {
      console.error('Failed to load future gifts:', err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not load your future gifts. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGifts();
  }, [fetchGifts, location.key]);

  /* ── Actions ── */
  const handleAddContent = (gift) => {
    if (!gift?.id) {
      console.warn(
        'Future gift has no id — run the backfill script before editing.'
      );
      return;
    }

    if (gift.giftType === 'magnets-frame') {
      setMagnetsModal({
        open: true,
        orderId: gift.orderId,
        giftId: gift.id,
        gift,
        orderNumber: gift.orderNumber
      });
      return;
    }

    if (gift.giftType === 'audio-gift') {
      setAudioModal({
        open: true,
        orderId: gift.orderId,
        giftId: gift.id,
        gift,
        orderNumber: gift.orderNumber
      });
      return;
    }

    /* Digital memory and anything else route to the creation page */
    navigate('/account/future-gifts/create-memory', {
      state: {
        gift,
        giftId: gift.id,
        giftIndex: gift.giftIndex,
        orderId: gift.orderId,
        orderNumber: gift.orderNumber,
        isEditing: false
      }
    });
  };

  /* 🟢 Opens the dedicated FutureGiftModal */
  const handleViewDetails = (gift) => {
    setDetailModal({
      open: true,
      order: gift.order,
      gift
    });
  };

  const closeDetailModal = () => {
    setDetailModal({ open: false, order: null, gift: null });
  };

  /* ── Helpers ── */
  const formatRevealDate = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const diff = new Date(dateStr).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const giftTitle = (gift) => {
    if (gift.title) return gift.title;
    switch (gift.giftType) {
      case 'digital-memory':
        return 'Digital Memory';
      case 'magnets-frame':
        return 'Memory Magnet';
      case 'audio-gift':
        return 'Audio Gift';
      default:
        return 'Future Gift';
    }
  };

  const truncateAddress = (addr = '') => {
    if (!addr) return '—';
    return addr.length > 60 ? `${addr.slice(0, 60)}…` : addr;
  };

  /* ── Render ── */
  return (
    <div className="px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C2A46] mb-2">
            Future Gifts
          </h1>
          <p className="text-sm sm:text-base text-[#6B635D]">
            Gifts you've scheduled to be revealed or delivered later.
          </p>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D2A146]" />
        </div>
      )}

      {/* Error */}
      {!isLoading && error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && gifts.length === 0 && (
        <div className="text-center py-8 sm:py-12">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <TimeIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
            No future gifts scheduled
          </h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            You don't have any gifts scheduled for future delivery yet.
          </p>
          <button
            onClick={() => navigate('/shop')}
            className="inline-flex items-center px-5 sm:px-6 py-2.5 sm:py-3 bg-[#D2A146] text-white font-medium rounded-md hover:bg-[#C49660] transition-colors text-sm sm:text-base"
          >
            Browse Shop
          </button>
        </div>
      )}

      {/* Gift cards */}
      {!isLoading && !error && gifts.length > 0 && (
        <div className="space-y-4 sm:space-y-5">
          {gifts.map((gift) => {
            const hasContent = !!gift.hasContent;
            const days = daysUntil(gift.deliveryDate);

            return (
              <div
                key={gift.id || `${gift.orderId}-${gift.giftIndex}`}
                className="border border-[#E4E4E4] w-full lg:w-2/3"
              >
                {/* ── Card header ── */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-[#E4E4E4] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-lg font-semibold text-[#2D3748] mb-1 truncate">
                      {giftTitle(gift)}
                    </h2>
                    <p className="text-xs sm:text-sm text-[#718096]">
                      {gift.orderDate}{' '}
                      {gift.orderTime ? `• ${gift.orderTime}` : ''}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                    <span className="px-3 py-1 text-[#0088FF] bg-[#0088FF]/10 text-xs font-medium rounded">
                      Scheduled
                    </span>
                    <button
                      onClick={() => handleViewDetails(gift)}
                      className="flex items-center gap-1 text-sm font-normal text-[#2D3748] hover:underline"
                    >
                      View Details
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* ── Card body ── */}
                <div className="px-4 sm:px-6 py-4 sm:py-5">
                  {/* Delivery + address + countdown */}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-xs sm:text-sm text-[#2D3748]">
                        <span className="text-[#718096]">
                          Delivery Scheduled:
                        </span>{' '}
                        {formatRevealDate(gift.deliveryDate)}
                      </p>
                      <p className="text-xs sm:text-sm text-[#2D3748] break-words">
                        <span className="text-[#718096]">Address:</span>{' '}
                        {truncateAddress(gift.shippingAddress)}
                      </p>
                      <p className="text-xs sm:text-sm text-[#55000C] underline">
                        Order #{gift.orderNumber || '—'}
                      </p>
                    </div>

                    {days !== null && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <TimeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#C08B2E]" />
                        <span className="text-xs sm:text-sm text-[#718096]">
                          {days} {days === 1 ? 'day' : 'days'} until Delivery
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content Required callout — only for gifts with no content yet */}
                  {!hasContent && (
                    <div className="mt-4 border border-dashed border-[#E6C77F] bg-[#FFF9EC] p-3 sm:p-4">
                      <h3 className="text-sm sm:text-base font-medium text-[#C08B2E] mb-1">
                        Content Required
                      </h3>
                      <p className="text-xs sm:text-sm text-[#6B635D] mb-3">
                        Future gift content needs to be uploaded before the
                        next delivery date.
                      </p>
                      <button
                        type="button"
                        onClick={() => handleAddContent(gift)}
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#55000C] hover:underline"
                      >
                        Upload Now
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Inline modals for content creation ── */}
      <MagnetsFrameModal
        isOpen={magnetsModal.open}
        onClose={() =>
          setMagnetsModal({
            open: false,
            orderId: null,
            giftId: null,
            gift: null,
            orderNumber: null
          })
        }
        orderId={magnetsModal.orderId}
        giftId={magnetsModal.giftId}
        gift={magnetsModal.gift}
        orderNumber={magnetsModal.orderNumber}
        onSaved={async () => {
          setMagnetsModal({
            open: false,
            orderId: null,
            giftId: null,
            gift: null,
            orderNumber: null
          });
          await fetchGifts();
        }}
      />

      <AudioGiftModal
        isOpen={audioModal.open}
        onClose={() =>
          setAudioModal({
            open: false,
            orderId: null,
            giftId: null,
            gift: null,
            orderNumber: null
          })
        }
        orderId={audioModal.orderId}
        giftId={audioModal.giftId}
        gift={audioModal.gift}
        orderNumber={audioModal.orderNumber}
        onSaved={async () => {
          setAudioModal({
            open: false,
            orderId: null,
            giftId: null,
            gift: null,
            orderNumber: null
          });
          await fetchGifts();
        }}
      />

      {/* 🟢 Separate detail modal */}
      <FutureGiftModal
        isOpen={detailModal.open}
        onClose={closeDetailModal}
        order={detailModal.order}
        gift={detailModal.gift}
        onRefresh={fetchGifts}
      />
    </div>
  );
};

export default FutureGifts;